import React, { useMemo } from 'react';
import TutorialMarkdownImageBlock from './TutorialMarkdownImageBlock';
import { parseImageLine } from '@/pages/tutorial/utils/tutorialMarkdownImages';

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function inlineFormat(text) {
  let html = escapeHtml(text);
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  return html;
}

function splitTableRow(line) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isTableSeparatorRow(cells) {
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function parseTableRows(lines) {
  const rows = lines.map(splitTableRow).filter((cells) => cells.some(Boolean));
  if (rows.length < 2) return null;
  const header = rows[0];
  const bodyRows = isTableSeparatorRow(rows[1]) ? rows.slice(2) : rows.slice(1);
  if (!header.length || bodyRows.length === 0) return null;
  return { header, rows: bodyRows };
}

function parseParamGridLine(line) {
  const match = line.trim().match(/^[-*]\s+(.+?)\s*\|\s*(.+?)\s*\|\s*(.+?)\s*\|\s*(.+)$/);
  if (!match) return null;
  return {
    name: match[1].trim(),
    badge: match[2].trim(),
    values: match[3].trim(),
    desc: match[4].trim(),
  };
}

function parseParamSnapshotLine(line) {
  const match = line.trim().match(/^[-*]\s+(.+?)\s*\|\s*(.+)$/);
  if (!match) return null;
  return { name: match[1].trim(), value: match[2].trim() };
}

function isRequiredBadge(badge) {
  return badge === '必填' || badge === '是';
}

function parseMarkdown(source) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    if (trimmed.startsWith('```')) {
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i += 1;
      }
      blocks.push({ type: 'code', content: codeLines.join('\n') });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('> ')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().slice(2));
        i += 1;
      }
      blocks.push({ type: 'callout', content: quoteLines.join(' ') });
      continue;
    }

    if (/^#{1,3}\s/.test(trimmed)) {
      const level = trimmed.match(/^#+/)[0].length;
      blocks.push({ type: `h${level}`, content: trimmed.replace(/^#+\s*/, '') });
      i += 1;
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    if (/^[-*]\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s*/, ''));
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s*/, ''));
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    if (trimmed === ':::param-grid') {
      const items = [];
      i += 1;
      while (i < lines.length && lines[i].trim() !== ':::') {
        const item = parseParamGridLine(lines[i]);
        if (item) items.push(item);
        i += 1;
      }
      if (items.length > 0) {
        blocks.push({ type: 'param-grid', items });
      }
      i += 1;
      continue;
    }

    if (trimmed === ':::param-snapshot') {
      const items = [];
      i += 1;
      while (i < lines.length && lines[i].trim() !== ':::') {
        const item = parseParamSnapshotLine(lines[i]);
        if (item) items.push(item);
        i += 1;
      }
      if (items.length > 0) {
        blocks.push({ type: 'param-snapshot', items });
      }
      i += 1;
      continue;
    }

    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i += 1;
      }
      const table = parseTableRows(tableLines);
      if (table) {
        blocks.push({ type: 'table', ...table });
        continue;
      }
      i -= tableLines.length;
    }

    const imageMeta = parseImageLine(trimmed);
    if (imageMeta) {
      blocks.push({ type: 'img', lineIndex: i, ...imageMeta });
      i += 1;
      continue;
    }

    const paragraphLines = [];
    while (i < lines.length && lines[i].trim() && !/^#{1,3}\s/.test(lines[i].trim())
      && !/^[-*]\s/.test(lines[i].trim()) && !/^\d+\.\s/.test(lines[i].trim())
      && !lines[i].trim().startsWith('> ') && !lines[i].trim().startsWith('```')
      && !parseImageLine(lines[i].trim()) && !/^(-{3,}|\*{3,}|_{3,})$/.test(lines[i].trim())) {
      paragraphLines.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: 'p', content: paragraphLines.join(' ') });
  }

  return blocks;
}

function groupImageRowBlocks(blocks) {
  const result = [];
  let index = 0;
  while (index < blocks.length) {
    const block = blocks[index];
    if (block.type !== 'img' || !block.row) {
      result.push(block);
      index += 1;
      continue;
    }
    const rowId = block.row;
    const images = [block];
    let next = index + 1;
    while (next < blocks.length && blocks[next].type === 'img' && blocks[next].row === rowId) {
      images.push(blocks[next]);
      next += 1;
    }
    if (images.length > 1) {
      result.push({
        type: 'img-row',
        rowId,
        align: images[0].align || 'center',
        images,
      });
    } else {
      result.push(block);
    }
    index = next;
  }
  return result;
}

export function extractHeadings(source) {
  let headingIndex = 0;
  return parseMarkdown(source)
    .filter((block) => block.type === 'h1' || block.type === 'h2' || block.type === 'h3')
    .map((block) => {
      const id = `heading-${headingIndex}-${block.content.replace(/\s+/g, '-').toLowerCase()}`;
      headingIndex += 1;
      return {
        id,
        level: Number(block.type.slice(1)),
        text: block.content,
      };
    });
}

export default function TutorialMarkdown({
  content,
  onHeadings,
  interactiveImages = false,
  selectedImageLineIndex = null,
  onSelectImage,
}) {
  const blocks = useMemo(() => groupImageRowBlocks(parseMarkdown(content)), [content]);
  const headings = useMemo(() => extractHeadings(content), [content]);

  React.useEffect(() => {
    if (onHeadings) onHeadings(headings);
  }, [headings, onHeadings]);

  let headingIndex = 0;

  return (
    <div
      className={`tutorial-markdown${interactiveImages ? ' tutorial-markdown--interactive' : ''}`}
      onClick={interactiveImages ? (event) => {
        if (event.target.closest('.tutorial-markdown__figure-block')) return;
        if (event.target.closest('.tutorial-markdown__figure-row')) return;
        if (event.target === event.currentTarget) return;
        onSelectImage?.(null);
      } : undefined}
    >
      {blocks.map((block, index) => {
        if (block.type === 'h1' || block.type === 'h2' || block.type === 'h3') {
          const Tag = block.type;
          const id = `heading-${headingIndex}-${block.content.replace(/\s+/g, '-').toLowerCase()}`;
          headingIndex += 1;
          return (
            <Tag key={`${block.type}-${index}`} id={id} className={`tutorial-markdown__${block.type}`}>
              {block.content}
            </Tag>
          );
        }
        if (block.type === 'p') {
          return (
            <p
              key={`p-${index}`}
              className="tutorial-markdown__p"
              dangerouslySetInnerHTML={{ __html: inlineFormat(block.content) }}
            />
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={`ul-${index}`} className="tutorial-markdown__ul">
              {block.items.map((item, itemIndex) => (
                <li
                  key={`ul-${index}-${itemIndex}`}
                  dangerouslySetInnerHTML={{ __html: inlineFormat(item) }}
                />
              ))}
            </ul>
          );
        }
        if (block.type === 'ol') {
          return (
            <ol key={`ol-${index}`} className="tutorial-markdown__ol">
              {block.items.map((item, itemIndex) => (
                <li
                  key={`ol-${index}-${itemIndex}`}
                  dangerouslySetInnerHTML={{ __html: inlineFormat(item) }}
                />
              ))}
            </ol>
          );
        }
        if (block.type === 'callout') {
          return (
            <div key={`callout-${index}`} className="tutorial-markdown__callout">
              <div
                dangerouslySetInnerHTML={{ __html: inlineFormat(block.content) }}
              />
            </div>
          );
        }
        if (block.type === 'code') {
          return (
            <pre key={`code-${index}`} className="tutorial-markdown__code">
              <code>{block.content}</code>
            </pre>
          );
        }
        if (block.type === 'hr') {
          return <hr key={`hr-${index}`} className="tutorial-markdown__hr" />;
        }
        if (block.type === 'param-grid') {
          return (
            <div key={`param-grid-${index}`} className="tutorial-markdown__param-grid">
              {block.items.map((item, itemIndex) => (
                <article
                  key={`param-grid-${index}-${itemIndex}`}
                  className={`tutorial-markdown__param-card${isRequiredBadge(item.badge) ? ' tutorial-markdown__param-card--required' : ''}`}
                >
                  <header className="tutorial-markdown__param-card-head">
                    <h4 className="tutorial-markdown__param-card-name">{item.name}</h4>
                    <span
                      className={`tutorial-markdown__param-badge${isRequiredBadge(item.badge) ? ' tutorial-markdown__param-badge--required' : ''}`}
                    >
                      {item.badge}
                    </span>
                  </header>
                  {item.values ? (
                    <p className="tutorial-markdown__param-card-values">{item.values}</p>
                  ) : null}
                  {item.desc ? (
                    <p
                      className="tutorial-markdown__param-card-desc"
                      dangerouslySetInnerHTML={{ __html: inlineFormat(item.desc) }}
                    />
                  ) : null}
                </article>
              ))}
            </div>
          );
        }
        if (block.type === 'param-snapshot') {
          return (
            <div key={`param-snapshot-${index}`} className="tutorial-markdown__param-snapshot">
              {block.items.map((item, itemIndex) => (
                <div key={`param-snapshot-${index}-${itemIndex}`} className="tutorial-markdown__param-snapshot-item">
                  <span className="tutorial-markdown__param-snapshot-name">{item.name}</span>
                  <span className="tutorial-markdown__param-snapshot-value">{item.value}</span>
                </div>
              ))}
            </div>
          );
        }
        if (block.type === 'table') {
          return (
            <div key={`table-${index}`} className="tutorial-markdown__table-wrap">
              <table className="tutorial-markdown__table">
                <thead>
                  <tr>
                    {block.header.map((cell, cellIndex) => (
                      <th key={`th-${index}-${cellIndex}`}>{cell}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={`tr-${index}-${rowIndex}`}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={`td-${index}-${rowIndex}-${cellIndex}`}
                          dangerouslySetInnerHTML={{ __html: inlineFormat(cell) }}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === 'img-row') {
          return (
            <div
              key={`img-row-${block.rowId}-${index}`}
              className={`tutorial-markdown__figure-row tutorial-markdown__figure-row--align-${block.align || 'center'}`}
            >
              {block.images.map((image) => (
                <TutorialMarkdownImageBlock
                  key={`img-${image.lineIndex}`}
                  meta={image}
                  lineIndex={image.lineIndex}
                  interactive={interactiveImages}
                  selected={selectedImageLineIndex === image.lineIndex}
                  onSelect={onSelectImage}
                  inRow
                />
              ))}
            </div>
          );
        }
        if (block.type === 'img') {
          return (
            <TutorialMarkdownImageBlock
              key={`img-${block.lineIndex}-${index}`}
              meta={block}
              lineIndex={block.lineIndex}
              interactive={interactiveImages}
              selected={selectedImageLineIndex === block.lineIndex}
              onSelect={onSelectImage}
            />
          );
        }
        return null;
      })}
    </div>
  );
}
